// =============================================================================
// Device runtime classification (SCDS fleet — 20-panel operation).
//
// `DeviceType` describes the screen (tv / portrait / projector). This module
// describes *what is running the Player* and therefore which remote features
// are real on that device:
//
//   web_browser    plain browser tab or kiosk PC — playback, refresh, identify,
//                  diagnostics only.
//   android_apk    BRICK Lit Android shell on ordinary hardware — adds app
//                  version, native status and manual APK update guidance.
//   hikvision_apk  the Android shell on a Hikvision signage panel whose vendor
//                  SDK actually answered — adds approved silent install, power,
//                  reboot, screen capture and launcher protection.
//
// The server never trusts a single field. Hikvision is only *confirmed* when
// the shell reports a Hikvision manufacturer, the SDK responded and a serial is
// known. An SDK response without manufacturer data (APK <= 0.4.7) stays
// *provisional*. Once the hardware fingerprint (manufacturer|model|serial) is
// recorded, any later change locks every privileged capability until a BO
// operator resets it — a swapped board or a spoofed report must not be able to
// power a panel down or install software.
// =============================================================================

export type PlayerRuntime = 'unknown' | 'web_browser' | 'android_apk';
export type HardwareVendor = 'unknown' | 'hikvision' | 'other';
export type DeviceClass = 'unknown' | 'web_browser' | 'android_apk' | 'hikvision_apk';
export type DeviceDetectionStatus = 'unverified' | 'provisional' | 'confirmed' | 'stale';

export type DeviceCapabilities = {
  power: boolean;
  reboot: boolean;
  screenCapture: boolean;
  silentInstall: boolean;
  launcherControl: boolean;
};

export type DeviceRuntimeProfile = {
  playerRuntime: PlayerRuntime;
  hardwareVendor: HardwareVendor;
  deviceClass: DeviceClass;
  detectionStatus: DeviceDetectionStatus;
  capabilities: DeviceCapabilities;
  hardwareManufacturer: string | null;
  hardwareModel: string | null;
  hardwareDevice: string | null;
  hardwareSerial: string | null;
  /** Last heartbeat that carried runtime evidence (shell report or explicit runtime). */
  capabilitiesReportedAt: string | null;
  /** manufacturer|model|serial recorded on first confirmation. */
  fingerprint: string | null;
  /** True once a later report disagreed with `fingerprint`; every privileged capability is off. */
  fingerprintLocked: boolean;
};

export const NO_CAPABILITIES: DeviceCapabilities = Object.freeze({
  power: false, reboot: false, screenCapture: false, silentInstall: false, launcherControl: false,
}) as DeviceCapabilities;

const HIKVISION_CAPABILITIES: DeviceCapabilities = Object.freeze({
  power: true, reboot: true, screenCapture: true, silentInstall: true, launcherControl: true,
}) as DeviceCapabilities;

export const UNKNOWN_RUNTIME_PROFILE: DeviceRuntimeProfile = Object.freeze({
  playerRuntime: 'unknown', hardwareVendor: 'unknown', deviceClass: 'unknown', detectionStatus: 'unverified',
  capabilities: NO_CAPABILITIES, hardwareManufacturer: null, hardwareModel: null, hardwareDevice: null, hardwareSerial: null,
  capabilitiesReportedAt: null, fingerprint: null, fingerprintLocked: false,
}) as DeviceRuntimeProfile;

/** Manufacturer strings the shell may report for Hikvision signage boards. */
const HIKVISION_MANUFACTURERS = [/hikvision/i, /^hik\b/i, /hangzhou\s*hikvision/i];

export function isHikvisionManufacturer(value: string | null | undefined): boolean {
  if (!value) return false;
  return HIKVISION_MANUFACTURERS.some(pattern => pattern.test(value.trim()));
}

/** Evidence one heartbeat supplies. Everything is optional; older Players send almost nothing. */
export type RuntimeEvidence = {
  /** Explicit runtime from Player >= 0.4.8 web build (`web_browser` or `android_apk`). */
  playerRuntime?: string | null | undefined;
  /** App version parsed from the Android shell user agent; absent in a browser. */
  appVersion?: string | null | undefined;
  nativeStatus?: {
    sdkAvailable?: boolean | undefined;
    serial?: string | undefined;
    manufacturer?: string | undefined;
    model?: string | undefined;
    device?: string | undefined;
    packageName?: string | undefined;
    playerRuntime?: string | undefined;
  } | null | undefined;
};

const text = (value: unknown, max = 128): string | null =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : null;

export function runtimeFingerprint(manufacturer: string | null, model: string | null, serial: string | null): string | null {
  if (!manufacturer && !model && !serial) return null;
  return [manufacturer ?? '', model ?? '', serial ?? ''].join('|');
}

/**
 * Merge one heartbeat into the stored profile. Pure: the caller persists the
 * result and raises an alert when `fingerprintLocked` flips to true.
 */
export function resolveDeviceRuntime(
  previous: DeviceRuntimeProfile | null | undefined,
  evidence: RuntimeEvidence,
  nowIso: string = new Date().toISOString(),
): DeviceRuntimeProfile {
  const before = previous ?? UNKNOWN_RUNTIME_PROFILE;
  const native = evidence.nativeStatus ?? null;
  const explicit = evidence.playerRuntime ?? native?.playerRuntime ?? null;

  let playerRuntime: PlayerRuntime = before.playerRuntime;
  if (explicit === 'android_apk' || explicit === 'web_browser') playerRuntime = explicit;
  else if (native || evidence.appVersion) playerRuntime = 'android_apk';
  // A heartbeat with no shell evidence at all comes from a browser. Do not
  // downgrade a device that has already proven it is an APK — a transient
  // heartbeat without nativeStatus (SDK probe timeout) must not flip class.
  else if (before.playerRuntime === 'unknown') playerRuntime = 'web_browser';

  const manufacturer = text(native?.manufacturer) ?? (playerRuntime === 'android_apk' ? before.hardwareManufacturer : null);
  const model = text(native?.model) ?? (playerRuntime === 'android_apk' ? before.hardwareModel : null);
  const deviceName = text(native?.device) ?? (playerRuntime === 'android_apk' ? before.hardwareDevice : null);
  const serial = text(native?.serial) ?? (playerRuntime === 'android_apk' ? before.hardwareSerial : null);
  const sdk = native?.sdkAvailable === true;

  let hardwareVendor: HardwareVendor = 'unknown';
  if (playerRuntime === 'web_browser') hardwareVendor = 'unknown';
  else if (manufacturer) hardwareVendor = isHikvisionManufacturer(manufacturer) ? 'hikvision' : 'other';
  else if (sdk) hardwareVendor = 'hikvision';
  else if (native) hardwareVendor = before.hardwareVendor === 'hikvision' && !manufacturer ? 'hikvision' : 'other';
  else hardwareVendor = before.hardwareVendor;

  let deviceClass: DeviceClass = 'unknown';
  if (playerRuntime === 'web_browser') deviceClass = 'web_browser';
  else if (playerRuntime === 'android_apk') deviceClass = hardwareVendor === 'hikvision' ? 'hikvision_apk' : 'android_apk';

  let detectionStatus: DeviceDetectionStatus = 'unverified';
  if (deviceClass === 'web_browser') detectionStatus = explicit === 'web_browser' ? 'confirmed' : 'provisional';
  else if (deviceClass === 'hikvision_apk') {
    const reportedNow = sdk && isHikvisionManufacturer(manufacturer) && !!serial;
    // Once a panel has been confirmed it stays confirmed. Power-off, a missed
    // heartbeat or one SDK probe timeout must not turn a registered Hikvision
    // panel back into "확인 중" — only a fingerprint mismatch (below) demotes it.
    detectionStatus = reportedNow || before.detectionStatus === 'confirmed' ? 'confirmed' : 'provisional';
  } else if (deviceClass === 'android_apk') detectionStatus = manufacturer ? 'confirmed' : 'provisional';

  // Fingerprint: recorded on first confirmed Hikvision report, compared afterwards.
  const current = runtimeFingerprint(manufacturer, model, serial);
  let fingerprint = before.fingerprint;
  let fingerprintLocked = before.fingerprintLocked;
  if (deviceClass === 'hikvision_apk' && detectionStatus === 'confirmed' && current) {
    if (!fingerprint) fingerprint = current;
    else if (fingerprint !== current) fingerprintLocked = true;
  }

  const privileged = deviceClass === 'hikvision_apk' && !fingerprintLocked
    && (detectionStatus === 'confirmed' || detectionStatus === 'provisional');
  const capabilities = privileged ? HIKVISION_CAPABILITIES : NO_CAPABILITIES;
  const carriesEvidence = !!native || !!explicit || !!evidence.appVersion;

  return {
    playerRuntime, hardwareVendor, deviceClass, detectionStatus, capabilities,
    hardwareManufacturer: manufacturer, hardwareModel: model, hardwareDevice: deviceName, hardwareSerial: serial,
    capabilitiesReportedAt: carriesEvidence ? nowIso : before.capabilitiesReportedAt,
    fingerprint, fingerprintLocked,
  };
}

/** Clear a fingerprint lock after a BO operator verified the hardware swap. */
export function resetRuntimeFingerprint(profile: DeviceRuntimeProfile): DeviceRuntimeProfile {
  return { ...profile, fingerprint: runtimeFingerprint(profile.hardwareManufacturer, profile.hardwareModel, profile.hardwareSerial),
    fingerprintLocked: false, capabilities: profile.deviceClass === 'hikvision_apk' ? HIKVISION_CAPABILITIES : NO_CAPABILITIES };
}

/** Evidence older than this is shown as "마지막 확인 n분 전" style hints only; the class itself never expires. */
export const RUNTIME_STALE_MS = 15 * 60_000;

/**
 * Status for display. A confirmed class is sticky: an offline panel is still
 * a Hikvision panel. Only an unconfirmed (provisional) profile that has not
 * reported recently reads as `stale`, so operators know the evidence is old.
 */
export function effectiveDetectionStatus(profile: DeviceRuntimeProfile | null | undefined, now = Date.now()): DeviceDetectionStatus {
  if (!profile) return 'unverified';
  if (profile.detectionStatus === 'unverified') return 'unverified';
  if (profile.detectionStatus === 'confirmed') return 'confirmed';
  const reported = Date.parse(profile.capabilitiesReportedAt ?? '');
  if (Number.isFinite(reported) && now - reported > RUNTIME_STALE_MS) return 'stale';
  return profile.detectionStatus;
}

export const DEVICE_CLASS_LABEL: Record<DeviceClass, string> = {
  unknown: '확인 중',
  web_browser: '웹 브라우저',
  android_apk: 'Android APK',
  hikvision_apk: 'HIKVISION APK',
};

export const DETECTION_STATUS_LABEL: Record<DeviceDetectionStatus, string> = {
  unverified: '미확인',
  provisional: '확인 중',
  confirmed: '확인됨',
  stale: '오래됨',
};

/** Which remote features the admin shows for a device. */
export type DeviceFeatureSet = {
  appVersion: boolean;
  updateRequests: boolean;
  manualUpdate: boolean;
  screenCapture: boolean;
  power: boolean;
  reboot: boolean;
};

export function deviceFeatureSet(profile: DeviceRuntimeProfile | null | undefined): DeviceFeatureSet {
  const cls = profile?.deviceClass ?? 'unknown';
  const caps = profile?.capabilities ?? NO_CAPABILITIES;
  return {
    appVersion: cls === 'android_apk' || cls === 'hikvision_apk',
    updateRequests: cls === 'hikvision_apk' && caps.silentInstall,
    manualUpdate: cls === 'android_apk' || (cls === 'hikvision_apk' && !caps.silentInstall),
    screenCapture: caps.screenCapture,
    power: caps.power,
    reboot: caps.reboot,
  };
}
